import { useMemo } from "react";
import { selectGondolas } from "@/entities/annotation";
import { useAppSelector } from "@/app/appStore";
import { GondolaProjectionPolygons } from "./gondola-projection-polygons";
import { ObservingCameraMask } from "@/shared/api";

export type GondolaProjection = {
  id: string;
  projection: [number, number][];
};

export type GondolaProjectionRendererProps = {
  isOverflownProjectionsVisible: boolean;
  gondolaProjections: GondolaProjection[];
  isOutsideView: (projection: [number, number][]) => boolean;
  isObserverCamera?: boolean;
};

export type GondolaProjectionRendererFn = ({
  gondolaProjections,
  isOutsideView,
}: GondolaProjectionRendererProps) => React.ReactNode;

type GondolaProjectionsProps = {
  selectedCamera: string | null;
  canvasDimensions: {
    width: number;
    height: number;
  };
  imageSize: {
    width: number;
    height: number;
  };
  observingCamerasMasks?: ObservingCameraMask[];
};

export function GondolaProjections({
  canvasDimensions,
  selectedCamera,
  imageSize,
  observingCamerasMasks = [],
}: GondolaProjectionsProps) {
  const gondolas = useAppSelector(selectGondolas);

  const gondolaProjections = useMemo(() => {
    if (!selectedCamera) return [];

    const selectedCameraMasks = observingCamerasMasks?.find(
      (camera) => camera.camera_local_id === selectedCamera
    );

    if (!selectedCameraMasks) return [];

    return Object.entries(selectedCameraMasks.masks)
      ?.filter(([id]) => gondolas.some((gondola) => gondola.name === id))
      .map(([id, projection]) => ({
        id,
        projection,
      }));
  }, [selectedCamera, gondolas, observingCamerasMasks]);

  const isOutsideView = useMemo(() => {
    return (polygon: [number, number][]) => {
      return polygon.every(
        ([x, y]) =>
          x < 0 || x > imageSize.width || y < 0 || y > imageSize.height
      );
    };
  }, [imageSize.width, imageSize.height]);

  if (gondolaProjections.length === 0) return null;

  return (
    <GondolaProjectionPolygons
      gondolaProjections={gondolaProjections}
      canvasDimensions={canvasDimensions}
      isOutsideView={isOutsideView}
      isOverflownProjectionsVisible={false}
    />
  );
}
